import Modal from '@/src/Common/components/Modal';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import { Dialog } from '@headlessui/react';
import { CopyIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

interface ActivationOption {
    id: number;
    name: string;
    date: string;
    isArchived: boolean;
}

export default function CopyToActivationModal({
    shareIdentifiers,
    activationId,
    onDismiss,
    onComplete,
}: {
    shareIdentifiers: string[];
    activationId: number;
    onDismiss: () => void;
    onComplete: () => void;
}) {
    const cancelButtonRef = useRef(null);
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const [activations, setActivations] = useState<ActivationOption[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCopying, setIsCopying] = useState(false);
    const [search, setSearch] = useState('');
    const [targetActivationId, setTargetActivationId] = useState<number | null>(
        null,
    );

    useEffect(() => {
        let isMounted = true;
        const loadActivations = async () => {
            try {
                const response = await authenticatedApi.get(
                    `${orgId}/activations/`,
                );

                if (!isMounted) {
                    return;
                }

                setActivations(
                    response.data
                        .filter(
                            (activation: ActivationOption) =>
                                activation.id !== activationId &&
                                !activation.isArchived,
                        )
                        .sort((a: ActivationOption, b: ActivationOption) =>
                            a.date > b.date ? -1 : 1,
                        ),
                );
            } catch (e) {
                console.error(e);
                toast.error('Error loading activations');
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        loadActivations();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, activationId]);

    const filteredActivations = activations.filter((activation) =>
        activation.name.toLowerCase().includes(search.trim().toLowerCase()),
    );

    const copy = async () => {
        if (!targetActivationId) {
            return;
        }

        setIsCopying(true);

        try {
            const response = await authenticatedApi.post(
                '/media/copy-to-activation',
                {
                    shareIdentifiers,
                    targetActivationId,
                },
            );

            const copied = response.data.copied ?? 0;
            const targetName = activations.find(
                (activation) => activation.id === targetActivationId,
            )?.name;

            if (copied > 0) {
                toast.success(
                    `Copied ${copied} ${copied === 1 ? 'photo' : 'photos'} to ${
                        targetName ?? 'activation'
                    }`,
                );
            } else {
                toast.info(
                    'No photos copied — they may already exist in that activation',
                );
            }

            onComplete();
        } catch (e) {
            console.error(e);
            toast.error('Error copying photos');
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <Modal cancelButtonRef={cancelButtonRef} onDismiss={onDismiss}>
            <div>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[99999px] bg-black sm:mx-0 sm:h-10 sm:w-10">
                            <CopyIcon
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                            />
                        </div>
                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                            <Dialog.Title
                                as="h3"
                                className="text-base font-semibold leading-6 text-gray-900"
                            >
                                Copy to Activation
                            </Dialog.Title>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    Copies {shareIdentifiers.length}{' '}
                                    {shareIdentifiers.length === 1
                                        ? 'photo'
                                        : 'photos'}{' '}
                                    into another activation's gallery. The
                                    photos are not duplicated in storage — the
                                    copies reuse the same files and keep their
                                    original timestamps.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="ml-12 mt-2 p-2">
                    {isLoading ? (
                        <p className="text-sm text-gray-500">
                            Loading activations...
                        </p>
                    ) : (
                        <div>
                            <label
                                htmlFor="activation-search"
                                className="block text-sm font-medium leading-6 text-gray-900"
                            >
                                Destination Activation
                            </label>
                            <div className="mt-2">
                                <input
                                    id="activation-search"
                                    type="text"
                                    placeholder="Search activations..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="block w-full p-2 rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                            <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-200">
                                {filteredActivations.length === 0 ? (
                                    <p className="p-3 text-sm text-gray-500">
                                        No activations found
                                    </p>
                                ) : (
                                    filteredActivations.map((activation) => (
                                        <div
                                            key={activation.id}
                                            className={`cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 ${
                                                targetActivationId ===
                                                activation.id
                                                    ? 'bg-blue-50 font-medium'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                setTargetActivationId(
                                                    activation.id,
                                                )
                                            }
                                        >
                                            <span>{activation.name}</span>
                                            <span className="ml-2 text-xs text-gray-400">
                                                {activation.date?.split('T')[0]}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                    type="button"
                    className="mt-3 inline-flex w-full ml-4 justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => onDismiss()}
                    ref={cancelButtonRef}
                >
                    Cancel
                </button>
                <br />
                <button
                    onClick={copy}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-black px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-800 hover:bg-black-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                    disabled={isCopying || !targetActivationId}
                >
                    {isCopying ? 'Copying...' : 'Copy Photos'}
                </button>
            </div>
        </Modal>
    );
}
